/**
 * Per-user calendar OAuth (Google / Microsoft). CSRF: random state maps to workspace + provider.
 */
const STORAGE_KEY = 'pr_calendar_oauth_state';
const TTL_MS = 10 * 60 * 1000;

interface StoredState {
  state: string;
  workspace_id: string;
  provider: 'google' | 'microsoft';
  ts: number;
}

export function storeCalendarOAuthState(
  workspaceId: string,
  provider: 'google' | 'microsoft',
): string {
  const state = crypto.randomUUID();
  const data: StoredState = { state, workspace_id: workspaceId, provider, ts: Date.now() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
  return state;
}

export function consumeCalendarOAuthState(state: string): {
  workspace_id: string;
  provider: 'google' | 'microsoft';
} | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data: StoredState = JSON.parse(raw);
    if (data.state !== state) return null;
    if (Date.now() - data.ts > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    localStorage.removeItem(STORAGE_KEY);
    return { workspace_id: data.workspace_id, provider: data.provider };
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function buildGoogleCalendarAuthUrl(params: {
  clientId: string;
  redirectUri: string;
  state: string;
}): string {
  const scope = [
    'openid',
    'email',
    'profile',
    // Needed to PATCH event descriptions during "Insert to Calendar", and to read event
    // attachments[] (e.g. native Google Meet recording / Gemini notes links surfaced when
    // the bot fails). Reading attachment metadata only needs calendar scope - we only ever
    // LINK to those files, never download their contents, so no Drive scope is requested.
    'https://www.googleapis.com/auth/calendar.events',
  ].join(' ');
  const u = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  u.searchParams.set('client_id', params.clientId);
  u.searchParams.set('redirect_uri', params.redirectUri);
  u.searchParams.set('response_type', 'code');
  u.searchParams.set('scope', scope);
  u.searchParams.set('access_type', 'offline');
  u.searchParams.set('prompt', 'consent');
  u.searchParams.set('state', params.state);
  return u.toString();
}

export function buildMicrosoftCalendarAuthUrl(params: {
  clientId: string;
  redirectUri: string;
  state: string;
}): string {
  const scope =
    // Needed to PATCH event bodies during "Insert to Calendar".
    ['offline_access', 'openid', 'profile', 'email', 'Calendars.ReadWrite', 'User.Read'].join(' ');
  const u = new URL('https://login.microsoftonline.com/common/oauth2/v2.0/authorize');
  u.searchParams.set('client_id', params.clientId);
  u.searchParams.set('redirect_uri', params.redirectUri);
  u.searchParams.set('response_type', 'code');
  u.searchParams.set('scope', scope);
  u.searchParams.set('state', params.state);
  return u.toString();
}
